import { classes } from 'html-classes';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton } from '~/components/isolated/IconButton';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage, createCategoryUrl } from '../utils';

import styles from './CategoriesCarouselSliderOneBlock.module.scss';

interface CategoriesCarouselSliderOneBlockProps {
  category: Category;
}

const CategoriesCarouselSliderOneBlock = ({
  category,
}: CategoriesCarouselSliderOneBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  if (!category.subcategory.length || !category.subcategory[0]?.id) {
    return null;
  }

  const items = category.subcategory.map(({ name, previewImage, id }) => ({
    id,
    node: (
      <div
        className={styles.item}
        style={{
          backgroundImage: `linear-gradient(to top, rgba(27, 26, 26, 1), rgba(27, 26, 26, 0)), url(${cfSrcForImage(
            previewImage,
            {
              width: 285,
              height: 380,
            },
          )})`,
        }}
      >
        <Link
          data-link={`category-${id}`}
          onClick={handleClickCategory(id)}
          to={createCategoryUrl({ id })}
          className={styles.link}
        >
          <div className={styles.slideTitle}>{name}</div>
        </Link>
      </div>
    ),
  }));

  const showArrows = !isMobile && items.length > 0;

  return (
    <div className={styles.container}>
      <CategoryBlockTitle
        text={category.name}
        className={styles.title}
        categoryId={category.id}
        linkTo={createCategoryUrl({ id: category.id })}
      />
      <div className={classes([styles.content, 'banner-carousel carousel'])}>
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onPrevButtonClick}
            className={classes([
              styles.carouselPrevButton,
              prevBtnDisabled && styles.hidden,
            ])}
          />
        )}
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: isMobile ? 3 : 4,
            containScroll: 'trimSnaps',
            active: items.length > 2,
            dragFree: isMobile,
          }}
          setApi={setApi}
        >
          <CarouselContent className={styles.carouselContent}>
            {items.map((item) => (
              <CarouselItem key={item.id} className={styles.slide}>
                {item.node}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onNextButtonClick}
            className={classes([
              styles.carouselNextButton,
              nextBtnDisabled && styles.hidden,
            ])}
          />
        )}
      </div>
    </div>
  );
};

export default CategoriesCarouselSliderOneBlock;
